/**
 * Centralized i18n configuration for path-based locale routing.
 *
 * German (de) is the default locale and served at root `/`.
 * Other locales are served at `/en/`, `/fr/`, `/it/`, `/es/`.
 */

export const SUPPORTED_LOCALES = [
  "de",
  "en",
  "fr",
  "it",
  "es",
  "pt",
  "nl",
  "pl",
  "sv",
  "cs",
  "el",
  "ro",
  "hu",
  "da",
  "fi",
  "no",
  "bg",
  "hr",
  "sk",
  "sl",
  "lt",
  "lv",
  "et",
] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];
export const DEFAULT_LOCALE: SupportedLocale = "de";

export const LOCALE_LABELS: Record<
  SupportedLocale,
  { label: string; flag: string }
> = {
  de: { label: "Deutsch", flag: "🇩🇪" },
  en: { label: "English", flag: "🇬🇧" },
  fr: { label: "Français", flag: "🇫🇷" },
  it: { label: "Italiano", flag: "🇮🇹" },
  es: { label: "Español", flag: "🇪🇸" },
  pt: { label: "Português", flag: "🇵🇹" },
  nl: { label: "Nederlands", flag: "🇳🇱" },
  pl: { label: "Polski", flag: "🇵🇱" },
  sv: { label: "Svenska", flag: "🇸🇪" },
  cs: { label: "Čeština", flag: "🇨🇿" },
  el: { label: "Ελληνικά", flag: "🇬🇷" },
  ro: { label: "Română", flag: "🇷🇴" },
  hu: { label: "Magyar", flag: "🇭🇺" },
  da: { label: "Dansk", flag: "🇩🇰" },
  fi: { label: "Suomi", flag: "🇫🇮" },
  no: { label: "Norsk", flag: "🇳🇴" },
  bg: { label: "Български", flag: "🇧🇬" },
  hr: { label: "Hrvatski", flag: "🇭🇷" },
  sk: { label: "Slovenčina", flag: "🇸🇰" },
  sl: { label: "Slovenščina", flag: "🇸🇮" },
  lt: { label: "Lietuvių", flag: "🇱🇹" },
  lv: { label: "Latviešu", flag: "🇱🇻" },
  et: { label: "Eesti", flag: "🇪🇪" },
};

/**
 * Type guard to check if a string is a valid supported locale.
 */
export function isValidLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/** Pages for Swiss employers, written in German only. Their URLs carry no locale. */
export const GERMAN_ONLY_PAGES = [
  "/temporaerbuero-alternative",
  "/personalvermittlung-kosten",
  "/jobboersen-schweiz-vergleich",
  "/mitarbeiter-finden",
  "/so-funktioniert-es",
];

// Blog and job pages take their language from their content, not the URL
const LOCALE_EXCLUDED_PATHS = ["/blog", "/job", ...GERMAN_ONLY_PAGES];

/**
 * Check if a path is excluded from locale routing (/blog, /job, German-only pages and below).
 */
export function isLocaleExcludedPath(path: string): boolean {
  return LOCALE_EXCLUDED_PATHS.some(
    excluded => path === excluded || path.startsWith(`${excluded}/`)
  );
}

/**
 * Extract locale from pathname.
 * Returns the locale if path starts with /en/, /fr/, /it/, /es/, otherwise returns default (de).
 *
 * Examples:
 *   /en/dashboard → "en"
 *   /fr/pricing → "fr"
 *   /dashboard → "de" (default)
 *   / → "de" (default)
 */
export function getLocaleFromPath(pathname: string): SupportedLocale {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && isValidLocale(firstSegment) && firstSegment !== "de") {
    return firstSegment;
  }

  return DEFAULT_LOCALE;
}

/**
 * Remove locale prefix from pathname.
 *
 * Examples:
 *   /en/dashboard → /dashboard
 *   /fr/pricing → /pricing
 *   /dashboard → /dashboard
 *   /en → /
 */
export function removeLocaleFromPath(pathname: string): string {
  const segments = pathname.split("/").filter(Boolean);
  const firstSegment = segments[0];

  if (firstSegment && isValidLocale(firstSegment) && firstSegment !== "de") {
    const rest = segments.slice(1).join("/");
    return rest ? `/${rest}` : "/";
  }

  return pathname;
}

/**
 * Add locale prefix to pathname.
 * German (de) paths stay at root, other locales get prefixed.
 *
 * Examples:
 *   addLocaleToPath("/dashboard", "en") → "/en/dashboard"
 *   addLocaleToPath("/dashboard", "de") → "/dashboard"
 *   addLocaleToPath("/", "fr") → "/fr"
 *   addLocaleToPath("/en/dashboard", "fr") → "/fr/dashboard" (replaces existing)
 */
export function addLocaleToPath(
  pathname: string,
  locale: SupportedLocale
): string {
  // First, remove any existing locale prefix
  const cleanPath = removeLocaleFromPath(pathname);

  // German stays at root
  if (locale === "de") {
    return cleanPath;
  }

  // Add locale prefix for non-German
  if (cleanPath === "/") {
    return `/${locale}`;
  }

  return `/${locale}${cleanPath}`;
}

/**
 * Map ISO 3166-1 alpha-2 country codes to preferred locale.
 */
export const COUNTRY_TO_LOCALE: Record<string, SupportedLocale> = {
  CH: "de",
  DE: "de",
  AT: "de",
  LI: "de",
  GB: "en",
  US: "en",
  CA: "en",
  AU: "en",
  NZ: "en",
  IE: "en",
  FR: "fr",
  BE: "fr",
  MC: "fr",
  LU: "fr",
  IT: "it",
  SM: "it",
  ES: "es",
  MX: "es",
  AR: "es",
  CL: "es",
  CO: "es",
  PT: "pt",
  BR: "pt",
  NL: "nl",
  PL: "pl",
  SE: "sv",
  CZ: "cs",
  GR: "el",
  CY: "el",
  RO: "ro",
  MD: "ro",
  HU: "hu",
  DK: "da",
  FI: "fi",
  NO: "no",
  BG: "bg",
  HR: "hr",
  SK: "sk",
  SI: "sl",
  LT: "lt",
  LV: "lv",
  EE: "et",
};

/**
 * Parse Accept-Language header into sorted list of supported locales.
 * Handles quality weights (q=0.8) and returns only supported locales.
 *
 * Example: "de-CH,de;q=0.9,en;q=0.8" → ["de", "en"]
 */
export function parseAcceptLanguage(header: string): SupportedLocale[] {
  if (!header) return [];

  const parsed = header
    .split(",")
    .map((part) => {
      const [langTag, ...params] = part.trim().split(";");
      const qParam = params.find((p) => p.trim().startsWith("q="));
      const quality = qParam ? parseFloat(qParam.trim().slice(2)) : 1.0;
      // Extract primary language subtag (e.g., "de-CH" → "de")
      const lang = langTag.trim().split("-")[0].toLowerCase();
      return { lang, quality };
    })
    .filter((entry) => !isNaN(entry.quality) && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality);

  // Deduplicate and keep only supported locales
  const seen = new Set<string>();
  const result: SupportedLocale[] = [];
  for (const entry of parsed) {
    if (!seen.has(entry.lang) && isValidLocale(entry.lang)) {
      seen.add(entry.lang);
      result.push(entry.lang as SupportedLocale);
    }
  }
  return result;
}

/**
 * Detect locale using geo IP country code and Accept-Language header.
 * Priority: country mapping → Accept-Language → default locale.
 */
export function detectLocaleFromGeo(
  countryCode: string,
  acceptLanguage: string
): SupportedLocale {
  const fromHeader = parseAcceptLanguage(acceptLanguage);

  // 1. Asking for German beats the country. AI crawlers mostly fetch from US
  // addresses and would otherwise be sent to /en.
  if (fromHeader[0] === DEFAULT_LOCALE) {
    return DEFAULT_LOCALE;
  }

  // 2. Try country mapping
  const upper = countryCode.toUpperCase();
  if (upper in COUNTRY_TO_LOCALE) {
    return COUNTRY_TO_LOCALE[upper];
  }

  // 3. Try Accept-Language
  if (fromHeader.length > 0) {
    return fromHeader[0];
  }

  // 4. Default
  return DEFAULT_LOCALE;
}

/**
 * Get all localized versions of a path for hreflang tags.
 * Used in sitemap and metadata generation.
 *
 * @param pathname - The path without locale prefix (e.g., "/pricing")
 * @param baseUrl - The base URL (e.g., "https://heyfinn.ai")
 * @returns Object with locale codes as keys and full URLs as values
 */
export function getLocalizedUrls(
  pathname: string,
  baseUrl: string
): Record<SupportedLocale | "x-default", string> {
  const cleanPath = removeLocaleFromPath(pathname);
  const suffix = cleanPath === "/" ? "" : cleanPath;

  return {
    de: `${baseUrl}${cleanPath}`,
    en: `${baseUrl}/en${suffix}`,
    fr: `${baseUrl}/fr${suffix}`,
    it: `${baseUrl}/it${suffix}`,
    es: `${baseUrl}/es${suffix}`,
    pt: `${baseUrl}/pt${suffix}`,
    nl: `${baseUrl}/nl${suffix}`,
    pl: `${baseUrl}/pl${suffix}`,
    sv: `${baseUrl}/sv${suffix}`,
    cs: `${baseUrl}/cs${suffix}`,
    el: `${baseUrl}/el${suffix}`,
    ro: `${baseUrl}/ro${suffix}`,
    hu: `${baseUrl}/hu${suffix}`,
    da: `${baseUrl}/da${suffix}`,
    fi: `${baseUrl}/fi${suffix}`,
    no: `${baseUrl}/no${suffix}`,
    bg: `${baseUrl}/bg${suffix}`,
    hr: `${baseUrl}/hr${suffix}`,
    sk: `${baseUrl}/sk${suffix}`,
    sl: `${baseUrl}/sl${suffix}`,
    lt: `${baseUrl}/lt${suffix}`,
    lv: `${baseUrl}/lv${suffix}`,
    et: `${baseUrl}/et${suffix}`,
    "x-default": `${baseUrl}${cleanPath}`,
  };
}
